import React, { useState, useEffect } from 'react';
import { Copy, Check, Send, Image, Video, FileText, ExternalLink, Loader2, CheckCircle, AlertCircle, Key, Globe, Calendar } from 'lucide-react';
import API from '../api/axios';
import { useWhatsApp } from '../context/WhatsAppContext';

const API_KEY = '6dc7d27843374c3113c662748363d4b7';
const BASE_URL = 'http://localhost:5001';

const APIDocs = () => {
  const { status } = useWhatsApp();
  const isConnected = status === 'connected';
  const [copied, setCopied] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Test Message');
  const [testImage, setTestImage] = useState('');
  const [testVideo, setTestVideo] = useState('');
  const [testPdf, setTestPdf] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTest, setActiveTest] = useState('text');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const buildUrl = (type) => {
    let url = `${BASE_URL}/send-message?apikey=${API_KEY}&phoneNumbers={phoneNumber}&message={message}`;
    if (type === 'image' && testImage) url += `&image=${encodeURIComponent(testImage)}`;
    if (type === 'video' && testVideo) url += `&video=${encodeURIComponent(testVideo)}`;
    if (type === 'pdf' && testPdf) url += `&pdf=${encodeURIComponent(testPdf)}`;
    return url;
  };

  const getExampleUrl = (type) => {
    const number = '916381226046';
    const msg = encodeURIComponent('Test Message');
    let url = `${BASE_URL}/send-message?apikey=${API_KEY}&phoneNumbers=${number}&message=${msg}`;
    if (type === 'image') url += `&image=${encodeURIComponent('https://smartzensolutions.com/assets/img/logo-smartzen-transparent.webp')}`;
    if (type === 'video') url += `&video=${encodeURIComponent('https://www.w3schools.com/html/mov_bbb.mp4')}`;
    if (type === 'pdf') url += `&pdf=${encodeURIComponent('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf')}`;
    return url;
  };

  const handleTestSend = async () => {
    if (!testNumber.trim()) return;
    setSending(true);
    setResult(null);

    try {
      let url = `/send-message?apikey=${API_KEY}&phoneNumbers=${testNumber.trim()}&message=${encodeURIComponent(testMessage)}`;
      if (activeTest === 'image' && testImage) url += `&image=${encodeURIComponent(testImage)}`;
      if (activeTest === 'video' && testVideo) url += `&video=${encodeURIComponent(testVideo)}`;
      if (activeTest === 'pdf' && testPdf) url += `&pdf=${encodeURIComponent(testPdf)}`;

      const res = await API.get(url);
      setResult(res.data);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  const testTypes = [
    { id: 'text', label: 'Text', icon: <Send size={16} />, color: 'blue' },
    { id: 'image', label: 'Image', icon: <Image size={16} />, color: 'purple' },
    { id: 'video', label: 'Video', icon: <Video size={16} />, color: 'orange' },
    { id: 'pdf', label: 'PDF', icon: <FileText size={16} />, color: 'red' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
              <Key size={20} className="text-white" />
            </div>
            API Documentation
          </h1>
          <p className="mt-2 text-slate-500">Use these endpoints to send WhatsApp messages programmatically.</p>
        </div>

        {/* Connection warning */}
        {!isConnected && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700">
            <AlertCircle size={20} className="flex-shrink-0" />
            <p className="text-sm font-medium">
              WhatsApp is not connected. Please{' '}
              <a href="/generate-qr" className="underline font-semibold">link your device</a> first.
            </p>
          </div>
        )}

        {/* API Key Card */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="space-y-3 flex-1">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">API Key</label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="px-4 py-2.5 bg-slate-100 rounded-lg text-sm font-mono text-slate-800 border border-slate-200 flex-1 break-all">
                    {API_KEY}
                  </code>
                  <button
                    onClick={() => copyToClipboard(API_KEY)}
                    className="p-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition"
                    title="Copy API Key"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-gray-400" />
                  Expires: <strong>17/Jul/2026</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <Globe size={14} className="text-gray-400" />
                  Method: <strong>GET</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* URL Format */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">URL Format</h2>
          <div className="bg-slate-900 text-green-400 rounded-xl p-4 font-mono text-sm overflow-x-auto">
            <span className="text-blue-400">GET</span>{' '}
            <span className="text-white">{BASE_URL}/send-message?apikey=</span>
            <span className="text-yellow-300">{'{APIKEY}'}</span>
            <span className="text-white">&phoneNumbers=</span>
            <span className="text-yellow-300">{'{MobileNumber}'}</span>
            <span className="text-white">&message=</span>
            <span className="text-yellow-300">{'{message}'}</span>
            <span className="text-gray-500">[&image=URL]</span>
            <span className="text-gray-500">[&video=URL]</span>
            <span className="text-gray-500">[&pdf=URL]</span>
          </div>
        </div>

        {/* Examples */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Examples</h2>
          <div className="space-y-4">
            {testTypes.map((type) => (
              <div key={type.id} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <span className={`p-1.5 rounded-lg ${
                    type.id === 'text' ? 'bg-blue-100 text-blue-600' :
                    type.id === 'image' ? 'bg-purple-100 text-purple-600' :
                    type.id === 'video' ? 'bg-orange-100 text-orange-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {type.icon}
                  </span>
                  <span className="font-medium text-sm text-gray-700">{type.label}</span>
                </div>
                <div className="p-4">
                  <div className="bg-slate-900 text-green-400 rounded-lg p-3 font-mono text-xs overflow-x-auto mb-3">
                    {getExampleUrl(type.id)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(getExampleUrl(type.id))}
                    className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    <Copy size={14} />
                    Copy URL
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Console */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ExternalLink size={18} className="text-green-500" />
            Test Console
          </h2>

          {/* Test type selector */}
          <div className="flex gap-2 flex-wrap mb-4">
            {testTypes.map(t => (
              <button
                key={t.id}
                onClick={() => { setActiveTest(t.id); setResult(null); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                  activeTest === t.id
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="text"
                placeholder="e.g. 916381226046"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>

            {activeTest === 'image' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/image.jpg"
                  value={testImage}
                  onChange={(e) => setTestImage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-300"
                />
              </div>
            )}

            {activeTest === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/video.mp4"
                  value={testVideo}
                  onChange={(e) => setTestVideo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>
            )}

            {activeTest === 'pdf' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PDF URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/document.pdf"
                  value={testPdf}
                  onChange={(e) => setTestPdf(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>
            )}

            <button
              onClick={handleTestSend}
              disabled={sending || !testNumber.trim() || !isConnected}
              className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                <><Send size={16} /> Send Test {activeTest === 'text' ? 'Message' : activeTest.charAt(0).toUpperCase() + activeTest.slice(1)}</>
              )}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className={`mt-4 flex items-start gap-3 p-4 rounded-xl border ${
              result.success
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {result.success
                ? <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                : <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              }
              <div>
                <p className="text-sm font-semibold">
                  {result.success ? 'Sent successfully!' : 'Failed'}
                </p>
                <p className="text-xs mt-0.5 opacity-70">{result.message}</p>
                {result.results && result.results.map((r, i) => (
                  <p key={i} className="text-xs mt-0.5 opacity-70">
                    {r.number}: {r.success ? `✅ ID: ${r.messageId}` : `❌ ${r.error}`}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default APIDocs;